import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import gsap from 'gsap'

export function SunsetPage() {
  const sectionRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation('landing')

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

      if (contentRef.current) {
        const elements = contentRef.current.querySelectorAll('.animate-item')
        tl.fromTo(
          elements,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.8, stagger: 0.15 },
          0.3
        )
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center py-24 px-4"
    >
      <div
        ref={contentRef}
        className="max-w-2xl mx-auto text-center space-y-8"
      >
        {/* Icon */}
        <div className="animate-item flex justify-center">
          <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center">
            <svg
              className="w-10 h-10 text-white/80"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className="animate-item font-heading-en text-4xl md:text-5xl text-white leading-tight">
          {t('sunset.title')}
        </h1>

        {/* Status */}
        <p className="animate-item text-lg text-white/60 font-body-ja">
          {t('sunset.date')}
        </p>

        {/* Message */}
        <div className="animate-item space-y-4 text-white/80 font-body-ja text-base leading-relaxed">
          <p>{t('sunset.message1')}</p>
          <p>{t('sunset.message2')}</p>
        </div>

        {/* Reason */}
        <div className="animate-item bg-white/5 rounded-xl p-6 text-left space-y-3">
          <h2 className="text-white font-bold text-lg">{t('sunset.reasonTitle')}</h2>
          <p className="text-white/70 text-sm leading-relaxed">
            {t('sunset.reasonDescription')}
          </p>
        </div>

        {/* Alternative */}
        <div className="animate-item bg-gradient-to-br from-violet-500/20 to-purple-600/20 rounded-xl p-6 text-left space-y-4 border border-violet-500/30">
          <h2 className="text-white font-bold text-lg flex items-center gap-2">
            <span>✨</span>
            {t('sunset.alternativeTitle')}
          </h2>
          <p className="text-white/80 text-sm leading-relaxed">
            {t('sunset.alternativeDescription')}
          </p>
          <Button variant="cta" size="lg" asChild className="w-full">
            <a
              href="https://github.com/hayaoo/deckclaw"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('sunset.alternativeCta')}
            </a>
          </Button>
        </div>

      </div>
    </section>
  )
}
